import React, { useState } from "react";
import type { ColumnsType } from "rc-table/es/interface";
import { toast } from "react-toastify";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";

import {
  Button,
  DataTable,
  ServerTablePagination,
  TableRowActionMenu,
} from "../components/ui";
import { ConnectLinkedinModal } from "../components/Modals";
import PurchaseCreditsModal from "../components/Teams/PurchaseCreditsModal";
import ConnectLinkedinCredentialsSidebar from "../components/Accounts/components/ConnectLinkedinCredentialsSidebar";
import LeadSenderAvatar from "../components/ui/LeadSenderAvatar";
import ConnectLinkedinExtensionSidebar from "../components/Accounts/components/ConnectLinkedinExtensionSidebar";
import LinkedinAccountCard from "../components/Accounts/components/LinkedinAccountCard";
import SenderLimitsSidebar, {
  DEFAULT_SENDER_DAILY_QUOTA_MAX,
  type SenderLimits,
} from "../components/Campaigns/components/SenderLimitsSidebar";
import axios from "../utils/axios";
import { disconnectLinkedinSender, fetchUserOwnersWithWorkspaces, updateExecutionMode } from "../utils";
import { ownerSelector } from "../store/store";

type AccountStatus = "Connected" | "Disconnected" | "LinkedIn login required";

type ConnectedAccountRow = {
  key: string;
  name: string;
  picture: string;
  status: AccountStatus;
  executionMode: "CLOUD" | "EXTENSION";
  automationStatus?: string;
  limits: {
    invitationsUsed: number;
    invitationsQuota: number;
    messagesUsed: number;
    messagesQuota: number;
    visitsUsed: number;
    visitsQuota: number;
  };
  raw: Record<string, any>;
};

type SenderEntitlements = {
  senderLimit: number | null;
  senderUsed: number;
  canAddSender: boolean;
  planKey: string;
  maxSeats: number | null;
};

const billingLinkClassName =
  "font-medium underline underline-offset-2 text-proxy-warning-text hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-blue rounded-sm";

const SENDER_LIMIT_BILLING_LINK_LABEL = "upgrade plan";

function renderSenderLimitMessageWithBillingLink(message: string) {
  const linkLabel = SENDER_LIMIT_BILLING_LINK_LABEL;
  const i = message.indexOf(linkLabel);
  if (i === -1) return message;
  return (
    <>
      {message.slice(0, i)}
      <Link to="/settings/billing" className={billingLinkClassName}>
        {linkLabel}
      </Link>
      {message.slice(i + linkLabel.length)}
    </>
  );
}

function SenderLimitNotice({ message }: { message: string }) {
  return <>{renderSenderLimitMessageWithBillingLink(message)}</>;
}

function AccountsConnectToolbar({
  hasLoadedAccounts,
  isSenderLimitReached,
  senderEntitlements,
  onConnect,
}: {
  hasLoadedAccounts: boolean;
  isSenderLimitReached: boolean;
  senderEntitlements: SenderEntitlements;
  onConnect: () => void;
}) {
  return (
    <div className="flex w-full justify-stretch sm:justify-end">
      <Button
        variant="primary"
        size="sm"
        className="w-full border-bright-green bg-bright-green text-dark-blue hover:brightness-95 sm:w-auto"
        data-bypass-plan-gate="true"
        disabled={isSenderLimitReached}
        onClick={() => {
          if (isSenderLimitReached) return;
          onConnect();
        }}
      >
        + Connect Account
      </Button>
    </div>
  );
}

export default function LinkedinAccounts() {
  const location = useLocation();
  const navigate = useNavigate();
  const { currentUser, currentWorkspace } = useSelector(ownerSelector);
  const resolvedUserId = React.useMemo(() => {
    const raw = currentUser?.userId;
    if (!raw) return "";
    if (typeof raw === "string") return raw.trim();
    if (typeof raw === "object" && (raw as { _id?: unknown })?._id) {
      return String((raw as { _id?: unknown })._id).trim();
    }
    return String(raw).trim();
  }, [currentUser?.userId]);
  const resolvedWorkspaceId = React.useMemo(() => {
    const workspace = currentWorkspace;
    if (!workspace) return "";
    if (typeof workspace === "string") {
      try {
        const parsed = JSON.parse(workspace);
        return String(parsed?._id || "").trim();
      } catch {
        return "";
      }
    }
    return String((workspace as { _id?: unknown })?._id || "").trim();
  }, [currentWorkspace]);
  const [showConnectModal, setShowConnectModal] = useState(false);
  const [showPurchaseSeatsModal, setShowPurchaseSeatsModal] = useState(false);
  const [showConnectCredentialsSidebar, setShowConnectCredentialsSidebar] = useState(false);
  const [showConnectExtensionSidebar, setShowConnectExtensionSidebar] = useState(false);
  const [isExtensionDetectionLoading, setIsExtensionDetectionLoading] = useState(false);
  const [connectSidebarMode, setConnectSidebarMode] = useState<"manual" | "extension">(
    "extension"
  );
  const [activeExtensionId, setActiveExtensionId] = useState("");
  const [reconnectOwnerId, setReconnectOwnerId] = useState("");
  const [connectedAccounts, setConnectedAccounts] = useState<Array<Record<string, any>>>([]);
  const [senderSearch, setSenderSearch] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalAccounts, setTotalAccounts] = useState(0);
  const [isLoadingAccounts, setIsLoadingAccounts] = useState(false);
  const [hasLoadedAccounts, setHasLoadedAccounts] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [accountsRefreshKey, setAccountsRefreshKey] = useState(0);
  const [disconnectConfirm, setDisconnectConfirm] = useState<{
    isOpen: boolean;
    ownerId: string;
    name: string;
  }>({ isOpen: false, ownerId: "", name: "" });
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const [disconnectError, setDisconnectError] = useState("");
  const [executionModeSwitch, setExecutionModeSwitch] = useState<{
    isOpen: boolean;
    ownerId: string;
    name: string;
    currentMode: "CLOUD" | "EXTENSION";
    nextMode: "CLOUD" | "EXTENSION";
  }>({
    isOpen: false,
    ownerId: "",
    name: "",
    currentMode: "EXTENSION",
    nextMode: "CLOUD",
  });
  const [isSwitchingExecutionMode, setIsSwitchingExecutionMode] = useState(false);
  const [executionModeSwitchError, setExecutionModeSwitchError] = useState("");
  const [isSenderLimitsOpen, setIsSenderLimitsOpen] = useState(false);
  const [selectedSenderForLimits, setSelectedSenderForLimits] = useState<{
    ownerId: string;
    name: string;
    picture: string;
  } | null>(null);
  const [senderLimits, setSenderLimits] = useState<SenderLimits>({
    maxFollowsPerDay: 0,
    maxMessagesPerDay: 0,
    maxConnectionRequestsPerDay: 0,
    maxProfileViewsPerDay: 0,
  });
  const [isSavingSenderLimits, setIsSavingSenderLimits] = useState(false);
  const [senderEntitlements, setSenderEntitlements] = useState<SenderEntitlements>({
    senderLimit: null,
    senderUsed: 0,
    canAddSender: true,
    planKey: "",
    maxSeats: null,
  });
  const lastKnownOwnerIdRef = React.useRef<string>("");
  const reconnectOwnerIdRef = React.useRef<string>("");

  React.useEffect(() => {
    reconnectOwnerIdRef.current = String(reconnectOwnerId || "").trim();
  }, [reconnectOwnerId]);

  React.useEffect(() => {
    if (location.state && (location.state as { openConnectModal?: boolean }).openConnectModal) {
      setShowConnectModal(true);
      navigate(location.pathname, { replace: true });
    }
  }, [location.pathname, location.state, navigate]);

  const getOwnerIdFromLocalStorage = React.useCallback((): string => {
    try {
      const ownerRaw = localStorage.getItem("owner");
      const parsedOwner = ownerRaw ? JSON.parse(ownerRaw) : null;
      const directId = parsedOwner?._id;
      const nestedOwnerId = parsedOwner?.owner?._id;
      const nestedDataId = parsedOwner?.data?._id;
      const maybeOwnerId = directId || nestedOwnerId || nestedDataId || "";
      return String(maybeOwnerId).trim();
    } catch {
      return "";
    }
  }, []);

  const getOwnerIdFromAny = React.useCallback((owner: any): string => {
    if (!owner) return "";
    const directId = owner?._id;
    const ownerId = owner?.ownerId;
    const nestedOwnerId = owner?.owner?._id;
    const nestedDataId = owner?.data?._id;
    return String(directId || ownerId || nestedOwnerId || nestedDataId || "").trim();
  }, []);

  const normalizeLinkedinEntityUrn = React.useCallback((value: unknown): string => {
    return String(value || "")
      .trim()
      .replace(/^urn:li:fsd_profile:/i, "")
      .replace(/^urn:li:fs_profile:/i, "")
      .replace(/\/+$/g, "")
      .toLowerCase();
  }, []);

  const normalizePublicIdentifier = React.useCallback((value: unknown): string => {
    const raw = String(value || "").trim();
    if (!raw) return "";
    const match = raw.match(/\/in\/([^/?#]+)/i);
    const id = match?.[1] ? match[1] : raw;
    return String(id).replace(/\/+$/g, "").trim().toLowerCase();
  }, []);

  const tableRows = React.useMemo<ConnectedAccountRow[]>(() => {
    return connectedAccounts.map((account) => ({
      key: String(account?._id || account?.ownerEntityUrn || account?.publicIdentifier || account?.name),
      name: account?.name || "LinkedIn account",
      picture: String(
        account?.picture || account?.profileImage || account?.profilePic || ""
      ).trim(),
      status: account?.isDisconnected
        ? "Disconnected"
        : String(account?.automationStatus || "") === "linkedin_login_required"
          ? "LinkedIn login required"
          : "Connected",
      executionMode:
        String(account?.executionMode || "").toUpperCase() === "EXTENSION"
          ? "EXTENSION"
          : "CLOUD",
      automationStatus: String(account?.automationStatus || ""),
      limits: {
        invitationsUsed: Number(account?.dailyUsage?.invitations || 0),
        invitationsQuota: Number(account?.dailyQuota?.invitations || 0),
        messagesUsed: Number(account?.dailyUsage?.messages || 0),
        messagesQuota: Number(account?.dailyQuota?.messages || 0),
        visitsUsed: Number(account?.dailyUsage?.visits || 0),
        visitsQuota: Number(account?.dailyQuota?.visits || 0),
      },
      raw: account,
    }));
  }, [connectedAccounts]);

  React.useEffect(() => {
    const q = new URLSearchParams(location.search).get("q") ?? "";
    if (q && q !== searchQuery) {
      setSenderSearch(q);
      setSearchQuery(q);
    }
  }, [location.search, searchQuery]);

  React.useEffect(() => {
    setPage(0);
  }, [searchQuery]);

  const loadLinkedinAccounts = React.useCallback(async (silent = false) => {
    if (!silent) setIsLoadingAccounts(true);
    if (!resolvedUserId) {
      setConnectedAccounts([]);
      setTotalAccounts(0);
      setIsLoadingAccounts(false);
      setHasLoadedAccounts(true);
      return;
    }
    try {
      const response = await fetchUserOwnersWithWorkspaces(
        page + 1,
        rowsPerPage,
        searchQuery,
        resolvedUserId,
        resolvedWorkspaceId || undefined
      );
      setConnectedAccounts(Array.isArray(response?.accounts) ? response.accounts : []);
      setTotalAccounts(Number(response?.pagination?.total || 0));
      const ent = response?.entitlements || null;
      const rawLimitValue = ent?.senderLimit;
      const rawLimit =
        rawLimitValue === null || rawLimitValue === undefined
          ? Number.POSITIVE_INFINITY
          : Number(rawLimitValue);
      const rawUsed = Number(ent?.senderUsed);
      const rawMaxSeats = Number(ent?.maxSeats);
      const hasFiniteLimit = Number.isFinite(rawLimit);
      setSenderEntitlements({
        senderLimit: hasFiniteLimit ? rawLimit : null,
        senderUsed: Number.isFinite(rawUsed) ? rawUsed : 0,
        canAddSender:
          typeof ent?.canAddSender === "boolean"
            ? ent.canAddSender
            : !hasFiniteLimit || (Number.isFinite(rawUsed) ? rawUsed : 0) < rawLimit,
        planKey: String(ent?.planKey || "").trim().toLowerCase(),
        maxSeats: Number.isFinite(rawMaxSeats) ? rawMaxSeats : null,
      });
    } catch {
      setConnectedAccounts([]);
      setTotalAccounts(0);
      setSenderEntitlements({
        senderLimit: null,
        senderUsed: 0,
        canAddSender: true,
        planKey: "",
        maxSeats: null,
      });
    } finally {
      setIsLoadingAccounts(false);
      setHasLoadedAccounts(true);
    }
  }, [page, rowsPerPage, searchQuery, resolvedUserId, resolvedWorkspaceId]);

  const isSenderLimitReached = React.useMemo(() => !senderEntitlements.canAddSender, [
    senderEntitlements.canAddSender,
  ]);

  const senderLimitMessage = React.useMemo(() => {
    if (!isSenderLimitReached) return "";

    const billingAction = "upgrade plan to connect more senders";

    if (
      senderEntitlements.senderLimit !== null &&
      Number.isFinite(senderEntitlements.senderLimit)
    ) {
      return `Sender limit reached (${senderEntitlements.senderUsed}/${senderEntitlements.senderLimit}). Disconnect an account or ${billingAction}.`;
    }

    return `Sender limit reached. Disconnect an account or ${billingAction}.`;
  }, [isSenderLimitReached, senderEntitlements.senderLimit, senderEntitlements.senderUsed]);

  React.useEffect(() => {
    loadLinkedinAccounts();
  }, [loadLinkedinAccounts, accountsRefreshKey]);

  React.useEffect(() => {
    const hasExtensionSender = connectedAccounts.some(
      (account) =>
        String(account?.executionMode || "").toUpperCase() === "EXTENSION" &&
        !account?.isDisconnected
    );
    if (!hasExtensionSender) return;

    const pollStatus = () => {
      if (document.visibilityState !== "visible") return;
      void loadLinkedinAccounts(true);
    };

    const intervalId = window.setInterval(pollStatus, 30_000);
    return () => window.clearInterval(intervalId);
  }, [connectedAccounts, loadLinkedinAccounts]);

  React.useEffect(() => {
    lastKnownOwnerIdRef.current = getOwnerIdFromLocalStorage();
  }, [getOwnerIdFromLocalStorage]);

  React.useEffect(() => {
    const triggerRefresh = () => {
      setAccountsRefreshKey((prev) => prev + 1);
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        triggerRefresh();
      }
    };

    const handleStorage = (event: StorageEvent) => {
      if (!event.key || event.key === "owner" || event.key === "currentWorkSpace") {
        triggerRefresh();
      }
    };

    window.addEventListener("focus", triggerRefresh);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("storage", handleStorage);

    return () => {
      window.removeEventListener("focus", triggerRefresh);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("storage", handleStorage);
    };
  }, []);

  const closeDisconnectModal = React.useCallback(() => {
    if (isDisconnecting) return;
    setDisconnectConfirm({ isOpen: false, ownerId: "", name: "" });
    setDisconnectError("");
  }, [isDisconnecting]);

  const confirmDisconnect = React.useCallback(async () => {
    if (!disconnectConfirm.ownerId || isDisconnecting) return;
    setIsDisconnecting(true);
    setDisconnectError("");
    try {
      await disconnectLinkedinSender(disconnectConfirm.ownerId);
      setConnectedAccounts((prev) =>
        prev.map((item) => {
          const itemId = getOwnerIdFromAny(item);
          if (itemId !== disconnectConfirm.ownerId) return item;
          return {
            ...item,
            isDisconnected: true,
            hasLinkedinToken: false,
          };
        })
      );
      // Refetch sender entitlements so Solo users can Connect/Reconnect without refresh.
      setAccountsRefreshKey((prev) => prev + 1);
      setDisconnectConfirm({ isOpen: false, ownerId: "", name: "" });
    } catch (error: any) {
      const msg =
        error?.response?.data?.msg ||
        error?.message ||
        "Unable to disconnect this account.";
      setDisconnectError(msg);
    } finally {
      setIsDisconnecting(false);
    }
  }, [disconnectConfirm.ownerId, getOwnerIdFromAny, isDisconnecting]);

  const closeExecutionModeSwitchModal = React.useCallback(() => {
    if (isSwitchingExecutionMode) return;
    setExecutionModeSwitch({
      isOpen: false,
      ownerId: "",
      name: "",
      currentMode: "EXTENSION",
      nextMode: "CLOUD",
    });
    setExecutionModeSwitchError("");
  }, [isSwitchingExecutionMode]);

  const confirmExecutionModeSwitch = React.useCallback(async () => {
    if (!executionModeSwitch.ownerId || isSwitchingExecutionMode) return;
    setIsSwitchingExecutionMode(true);
    setExecutionModeSwitchError("");
    try {
      const response = await updateExecutionMode(
        executionModeSwitch.ownerId,
        executionModeSwitch.nextMode
      );
      setConnectedAccounts((prev) =>
        prev.map((item) => {
          const itemId = getOwnerIdFromAny(item);
          if (itemId !== executionModeSwitch.ownerId) return item;
          return {
            ...item,
            executionMode: executionModeSwitch.nextMode,
            automationStatus:
              executionModeSwitch.nextMode === "CLOUD" ? "cloud" : item.automationStatus,
          };
        })
      );
      toast.success(
        response?.msg ||
          (executionModeSwitch.nextMode === "CLOUD"
            ? "Switched to server automation."
            : "Switched to Chrome extension automation.")
      );
      closeExecutionModeSwitchModal();
      setAccountsRefreshKey((prev) => prev + 1);
    } catch (error: any) {
      const msg =
        error?.response?.data?.msg ||
        error?.message ||
        "Unable to switch automation method.";
      setExecutionModeSwitchError(msg);
    } finally {
      setIsSwitchingExecutionMode(false);
    }
  }, [
    closeExecutionModeSwitchModal,
    executionModeSwitch.nextMode,
    executionModeSwitch.ownerId,
    getOwnerIdFromAny,
    isSwitchingExecutionMode,
  ]);

  const openExecutionModeSwitch = React.useCallback(
    (row: ConnectedAccountRow) => {
      if (row.status !== "Connected") return;
      const ownerId = getOwnerIdFromAny(row.raw);
      if (!ownerId) {
        toast.error("Unable to resolve sender");
        return;
      }
      const nextMode = row.executionMode === "EXTENSION" ? "CLOUD" : "EXTENSION";
      setExecutionModeSwitchError("");
      setExecutionModeSwitch({
        isOpen: true,
        ownerId,
        name: row.name || "this account",
        currentMode: row.executionMode,
        nextMode,
      });
    },
    [getOwnerIdFromAny]
  );

  const openSenderLimitsSidebar = React.useCallback((row: ConnectedAccountRow) => {
    const ownerId = getOwnerIdFromAny(row.raw);
    if (!ownerId) {
      toast.error("Unable to resolve sender");
      return;
    }
    const dailyQuota = row.raw?.dailyQuota ?? {};
    setSenderLimits({
      maxFollowsPerDay: Number(dailyQuota?.follows ?? 0),
      maxMessagesPerDay: Number(dailyQuota?.messages ?? 0),
      maxConnectionRequestsPerDay: Number(dailyQuota?.invitations ?? 0),
      maxProfileViewsPerDay: Number(dailyQuota?.visits ?? 0),
    });
    setSelectedSenderForLimits({
      ownerId,
      name: row.name,
      picture: row.picture,
    });
    setIsSenderLimitsOpen(true);
  }, [getOwnerIdFromAny]);

  const handleSaveSenderLimits = React.useCallback(async () => {
    if (!selectedSenderForLimits?.ownerId) {
      toast.error("Unable to resolve sender");
      return;
    }
    setIsSavingSenderLimits(true);
    try {
      await axios.post("/owner/updateDailyQuota", {
        ownerId: selectedSenderForLimits.ownerId,
        dailyQuota: {
          follows: Number(senderLimits.maxFollowsPerDay || 0),
          invitations: Number(senderLimits.maxConnectionRequestsPerDay || 0),
          messages: Number(senderLimits.maxMessagesPerDay || 0),
          visits: Number(senderLimits.maxProfileViewsPerDay || 0),
        },
      });
      toast.success("Sender limits updated successfully");
      setConnectedAccounts((prev) =>
        prev.map((item) => {
          const itemId = String(item?._id || item?.ownerId || "").trim();
          if (itemId !== selectedSenderForLimits.ownerId) return item;
          return {
            ...item,
            dailyQuota: {
              ...(item?.dailyQuota || {}),
              follows: Number(senderLimits.maxFollowsPerDay || 0),
              invitations: Number(senderLimits.maxConnectionRequestsPerDay || 0),
              messages: Number(senderLimits.maxMessagesPerDay || 0),
              visits: Number(senderLimits.maxProfileViewsPerDay || 0),
            },
          };
        })
      );
      setIsSenderLimitsOpen(false);
    } catch (error) {
      console.log(error);
      toast.error("Unable to update sender limits");
    } finally {
      setIsSavingSenderLimits(false);
    }
  }, [selectedSenderForLimits, senderLimits]);

  const accountColumns = React.useMemo<ColumnsType<ConnectedAccountRow>>(
    () => [
      {
        title: "LinkedIn Account",
        dataIndex: "name",
        key: "name",
        width: 350,
        render: (_value, row) => (
          <div className="flex items-center gap-2">
            <LeadSenderAvatar picture={row.picture} className="h-7 w-7" />
            <span>{row.name}</span>
          </div>
        ),
      },
      {
        title: "Status",
        dataIndex: "status",
        key: "status",
        width: 180,
        render: (value: ConnectedAccountRow["status"]) => (
          <span
            className={`inline-flex h-fit w-fit rounded-[4px] px-2 py-1 text-xs ${
              value === "Connected"
                ? "bg-success-bg text-success"
                : value === "LinkedIn login required"
                  ? "bg-yellow-100 text-yellow-800"
                  : "bg-light-grey text-dark-grey"
            }`}
          >
            {value}
          </span>
        ),
      },
      {
        title: "Method",
        dataIndex: "executionMode",
        key: "executionMode",
        width: 160,
        render: (value: ConnectedAccountRow["executionMode"], row) => {
          const workerLabel =
            value === "CLOUD"
              ? "Server-side"
              : row.automationStatus === "online"
                ? "Online"
                : row.automationStatus === "paused"
                  ? "Paused"
                  : row.automationStatus === "linkedin_login_required"
                    ? "LinkedIn login required"
                    : "Offline";
          const switchLabel =
            value === "EXTENSION" ? "Use server instead" : "Use Chrome instead";
          return (
            <div className="flex flex-col gap-1">
              <span className="inline-flex h-fit w-fit rounded-[4px] bg-bg-light-blue px-2 py-1 text-xs text-brand-blue">
                {value === "CLOUD" ? "Cloud" : "Extension"}
              </span>
              {workerLabel ? (
                <span className="text-[11px] text-dark-grey">{workerLabel}</span>
              ) : null}
              {row.status === "Connected" ? (
                <button
                  type="button"
                  onClick={() => openExecutionModeSwitch(row)}
                  className="w-fit text-left text-[11px] font-medium text-brand-blue underline underline-offset-2 hover:opacity-80"
                >
                  {switchLabel}
                </button>
              ) : null}
            </div>
          );
        },
      },
      {
        title: "Sending limits",
        dataIndex: "limits",
        key: "limits",
        width: 300,
        render: (limits: ConnectedAccountRow["limits"], row) => (
          <div className="flex items-center justify-between gap-2 text-xs">
            <div className="flex items-center gap-4">
              <span>{`${limits.invitationsUsed}/${limits.invitationsQuota}`}</span>
              <span>{`${limits.messagesUsed}/${limits.messagesQuota}`}</span>
              <span>{`${limits.visitsUsed}/${limits.visitsQuota}`}</span>
            </div>
            <button
              type="button"
              aria-label="Open sender limits"
              onClick={() => openSenderLimitsSidebar(row)}
              className="inline-flex h-7 w-7 items-center justify-center rounded-[8px] hover:bg-background-grey"
            >
              <img src="/icons/setting.svg" alt="" className="h-4 w-4" />
            </button>
          </div>
        ),
      },
      {
        title: "",
        dataIndex: "key",
        key: "actions",
        width: 70,
        render: (_value, row) => (
          <div className="flex justify-end">
            <TableRowActionMenu
              onDelete={() => {
                if (row.status === "Disconnected") {
                  if (isSenderLimitReached) return;
                  const ownerId = getOwnerIdFromAny(row.raw);
                  if (!ownerId) {
                    toast.error("Unable to resolve sender");
                    return;
                  }
                  setReconnectOwnerId(ownerId);
                  setConnectSidebarMode("manual");
                  setShowConnectModal(true);
                  return;
                }
                const ownerId = getOwnerIdFromAny(row.raw);
                setDisconnectError("");
                setDisconnectConfirm({
                  isOpen: true,
                  ownerId,
                  name: row.name || "this account",
                });
              }}
              deleteLabel={row.status === "Disconnected" ? "Reconnect" : "Disconnect"}
              deleteDisabled={row.status === "Disconnected" && isSenderLimitReached}
              className="mr-1"
            />
          </div>
        ),
      },
    ],
    [getOwnerIdFromAny, isSenderLimitReached, openExecutionModeSwitch, openSenderLimitsSidebar]
  );

  const getExtensionId = () => {
    const marker = document.getElementById("SCR_Extension_Installed");
    const markerExtensionId = marker?.getAttribute("data-extension-id") || "";
    const maybeWindow = window as Window & {
      __INNLEAD_EXTENSION_ID__?: string;
    };
    const webStoreUrl = process.env.REACT_APP_CHROME_WEBSTORE_URL || "";
    const webStoreMatch = webStoreUrl.match(/\/detail\/[^/]+\/([a-z]{32})(?:[/?#]|$)/i);
    const extensionIdFromStoreUrl = webStoreMatch?.[1] || "";
    return (
      markerExtensionId ||
      maybeWindow.__INNLEAD_EXTENSION_ID__ ||
      process.env.REACT_APP_CHROME_EXTENSION_ID ||
      extensionIdFromStoreUrl ||
      ""
    );
  };

  const pingInstalledExtension = async () => {
    const extensionId = getExtensionId();
    const browserChrome = (window as Window & { chrome?: any }).chrome;
    const chromeRuntime = browserChrome?.runtime;
    if (!extensionId || !chromeRuntime?.sendMessage) {
      return false;
    }

    return new Promise<boolean>((resolve) => {
      let resolved = false;
      const timeout = window.setTimeout(() => {
        if (!resolved) {
          resolved = true;
          resolve(false);
        }
      }, 1000);

      try {
        chromeRuntime.sendMessage(extensionId, { type: "PING_INNLEAD_EXTENSION" }, () => {
          if (resolved) return;
          window.clearTimeout(timeout);
          resolved = true;
          const chromeLastError = browserChrome?.runtime?.lastError;
          resolve(!chromeLastError);
        });
      } catch {
        if (!resolved) {
          window.clearTimeout(timeout);
          resolved = true;
          resolve(false);
        }
      }
    });
  };

  const detectExtensionInstalled = async () => {
    // Marker globals can remain stale until full page refresh.
    // Runtime ping is the authoritative signal for installed/uninstalled state.
    const firstCheck = await pingInstalledExtension();
    if (firstCheck) return true;

    // Small retry window for transient runtime readiness.
    await new Promise((resolve) => window.setTimeout(resolve, 300));
    return pingInstalledExtension();
  };

  React.useEffect(() => {
    if (!showConnectExtensionSidebar || isExtensionDetectionLoading) return;

    let disposed = false;
    const interval = window.setInterval(async () => {
      const installed = await detectExtensionInstalled();
      if (!installed || disposed) return;
      setActiveExtensionId(getExtensionId());
      setConnectSidebarMode("extension");
      setShowConnectCredentialsSidebar(true);
      setShowConnectExtensionSidebar(false);
      setShowConnectModal(false);
    }, 1200);

    return () => {
      disposed = true;
      window.clearInterval(interval);
    };
  }, [showConnectExtensionSidebar, isExtensionDetectionLoading]);

  const cardRows = React.useMemo(
    () =>
      tableRows.map((row) => ({
        key: row.key,
        name: row.name,
        picture: row.picture,
        status: row.status,
        executionMode: row.executionMode,
        automationStatus: row.automationStatus,
        automationPaused: Boolean(row.raw?.automationPaused),
        activeCampaignDisplayName:
          String(
            row.raw?.primaryCampaignName ||
              row.raw?.activeCampaignDisplayName ||
              row.raw?.primaryCampaignId ||
              "No active campaign"
          ).trim() || "No active campaign",
        primaryCampaignId: String(row.raw?.primaryCampaignId || "").trim(),
        hasActiveRunningCampaign: Boolean(row.raw?.hasActiveRunningCampaign),
        limits: row.limits,
        raw: row.raw,
      })),
    [tableRows]
  );

  const shouldShowPagination = totalAccounts > rowsPerPage;

  return (
    <section className="noScroll flex h-full min-h-0 flex-col overflow-y-auto overflow-x-hidden bg-background-grey px-3 pb-4 sm:px-6 sm:pb-6">
      <header className="shrink-0 py-4 pt-6 sm:py-6 sm:pt-8">
        <h1 className="text-xl font-semibold tracking-[-0.02em] text-dark-black sm:text-2xl">
          Sending Accounts
        </h1>
        <p className="mt-2 text-sm text-dark-grey">
          Manage account health and availability across your team.
        </p>
      </header>

      <div className="relative flex min-h-0 flex-1 flex-col overflow-visible rounded-[8px] bg-background-grey">
        <div className="flex shrink-0 flex-col gap-4 bg-background-grey py-0">
          {isSenderLimitReached ? (
            <div className="rounded-[8px] border border-proxy-warning-border bg-proxy-warning-bg px-3 py-2">
              <p className="text-xs text-proxy-warning-text">
                <SenderLimitNotice message={senderLimitMessage} />
              </p>
            </div>
          ) : null}
          <div className="flex w-full justify-end">
            <AccountsConnectToolbar
              hasLoadedAccounts={hasLoadedAccounts}
              isSenderLimitReached={isSenderLimitReached}
              senderEntitlements={senderEntitlements}
              onConnect={() => {
                setReconnectOwnerId("");
                setShowConnectModal(true);
              }}
            />
          </div>
        </div>

        <div className="mt-4 flex min-h-0 flex-1 flex-col bg-background-grey">
          <div className="min-h-0 flex-1 rounded-[8px] bg-background-grey">
            {isLoadingAccounts && (!hasLoadedAccounts || connectedAccounts.length === 0) ? (
              <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                {Array.from({ length: 3 }).map((_, index) => (
                  <div key={index} className="rounded-[14px] border border-line bg-white p-4">
                    <div className="h-11 w-11 animate-pulse rounded-full bg-light-grey" />
                    <div className="mt-4 h-4 w-28 animate-pulse rounded bg-light-grey" />
                    <div className="mt-2 h-3 w-24 animate-pulse rounded bg-light-grey" />
                    <div className="mt-4 h-20 animate-pulse rounded-[10px] bg-light-grey/80" />
                    <div className="mt-4 grid grid-cols-3 gap-2">
                      {Array.from({ length: 3 }).map((__, innerIndex) => (
                        <div key={innerIndex} className="h-12 animate-pulse rounded-[8px] bg-light-grey/80" />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : cardRows.length > 0 ? (
              <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                {cardRows.map((row) => (
                  <LinkedinAccountCard
                    key={row.key}
                    row={row}
                    isSenderLimitReached={isSenderLimitReached}
                    onOpenSettings={(senderRow) => {
                      const match = tableRows.find((item) => item.key === senderRow.key);
                      if (!match) return;
                      openSenderLimitsSidebar(match);
                    }}
                    onSwitchExecutionMode={(senderRow) => {
                      const match = tableRows.find((item) => item.key === senderRow.key);
                      if (!match) return;
                      openExecutionModeSwitch(match);
                    }}
                    onDisconnectOrReconnect={(senderRow) => {
                      const match = tableRows.find((item) => item.key === senderRow.key);
                      if (!match) return;

                      if (match.status === "Disconnected") {
                        if (isSenderLimitReached) return;
                        const ownerId = getOwnerIdFromAny(match.raw);
                        if (!ownerId) {
                          toast.error("Unable to resolve sender");
                          return;
                        }
                        setReconnectOwnerId(ownerId);
                        setConnectSidebarMode("manual");
                        setShowConnectModal(true);
                        return;
                      }
                      const ownerId = getOwnerIdFromAny(match.raw);
                      setDisconnectError("");
                      setDisconnectConfirm({
                        isOpen: true,
                        ownerId,
                        name: match.name || "this account",
                      });
                    }}
                  />
                ))}
              </div>
            ) : (
              <div className="flex min-h-[220px] items-center justify-center rounded-[12px] border border-dashed border-light-grey bg-background-grey/40 text-center">
                <div>
                  <p className="text-base font-semibold text-dark-black">No LinkedIn accounts yet</p>
                  <p className="mt-1 text-sm text-dark-grey">Connect a sender to start running campaigns.</p>
                </div>
              </div>
            )}

            {shouldShowPagination ? (
              <div className="mt-4">
                <ServerTablePagination
                  className="sticky bottom-0"
                  page={page}
                  pageSize={rowsPerPage}
                  total={totalAccounts}
                  rowsPerPageOptions={[10, 15, 25, 50]}
                  onPageChange={setPage}
                  onPageSizeChange={(nextSize) => {
                    setRowsPerPage(nextSize);
                    setPage(0);
                  }}
                  loading={isLoadingAccounts}
                />
              </div>
            ) : null}
          </div>
        </div>
      </div>

      <ConnectLinkedinModal
        isOpen={showConnectModal}
        onClose={() => setShowConnectModal(false)}
        onOpenCredentialsSidebar={() => {
          // Reconnect flow sets `reconnectOwnerId` before opening this modal.
          // Keep that context so credentials flow remains scoped to the selected sender.
          setConnectSidebarMode("manual");
          setActiveExtensionId(getExtensionId());
          setShowConnectCredentialsSidebar(true);
          setShowConnectModal(false);
        }}
        onLoginWithExtension={async () => {
          setIsExtensionDetectionLoading(true);
          // Open the install sidebar immediately so the user sees a loader
          // while `detectExtensionInstalled()` runs.
          setShowConnectExtensionSidebar(true);
          const extensionInstalled = await detectExtensionInstalled();
          if (extensionInstalled) {
            setActiveExtensionId(getExtensionId());
            setConnectSidebarMode("extension");
            setShowConnectCredentialsSidebar(true);
            setShowConnectExtensionSidebar(false);
            setIsExtensionDetectionLoading(false);
            setShowConnectModal(false);
            return;
          }
          setShowConnectModal(false);
          setIsExtensionDetectionLoading(false);
        }}
      />

      <ConnectLinkedinCredentialsSidebar
        isOpen={showConnectCredentialsSidebar}
        reconnectOwnerId={reconnectOwnerId}
        onClose={() => {
          setShowConnectCredentialsSidebar(false);
          setReconnectOwnerId("");
          // Extension flow may update `localStorage.owner` via background injection.
          // Delay slightly and refresh listing if owner changed.
          window.setTimeout(() => {
            const nextOwnerId = getOwnerIdFromLocalStorage();
            if (nextOwnerId && nextOwnerId !== lastKnownOwnerIdRef.current) {
              lastKnownOwnerIdRef.current = nextOwnerId;
              setAccountsRefreshKey((prev) => prev + 1);
            }
          }, 500);
        }}
        onBack={() => {
          setShowConnectCredentialsSidebar(false);
          setIsExtensionDetectionLoading(false);
          setShowConnectModal(true);
        }}
        mode={connectSidebarMode}
        extensionId={activeExtensionId}
        onConnected={(owner) => {
          const reconnectTarget = reconnectOwnerIdRef.current;
          const wasReconnectFlow = Boolean(reconnectTarget);
          setPage(0);
          setSearchQuery("");
          setSenderSearch("");
          // Show newly connected owner immediately while server refresh catches up.
          setConnectedAccounts((prev) => {
            const nextId = getOwnerIdFromAny(owner);
            const nextUrn = normalizeLinkedinEntityUrn(owner?.ownerEntityUrn);
            const nextPub = normalizePublicIdentifier(owner?.publicIdentifier);

            const rowMatchesOwner = (item: Record<string, any>): boolean => {
              const itemId = getOwnerIdFromAny(item);
              if (nextId && itemId && itemId === nextId) return true;
              if (reconnectTarget && itemId && itemId === reconnectTarget) return true;
              const itemUrn = normalizeLinkedinEntityUrn(item?.ownerEntityUrn);
              if (nextUrn && itemUrn && itemUrn === nextUrn) return true;
              const itemPub = normalizePublicIdentifier(item?.publicIdentifier);
              if (nextPub && itemPub && itemPub === nextPub) return true;
              return false;
            };

            const buildRowFromOwner = (
              o: Record<string, any>,
              idFallback: string
            ): Record<string, any> => {
              const id = getOwnerIdFromAny(o) || idFallback;
              return {
                _id: id,
                name: o?.name || "LinkedIn account",
                email: o?.email || "",
                picture: String(
                  o?.picture || o?.profileImage || o?.profilePic || ""
                ).trim(),
                publicIdentifier: o?.publicIdentifier || "",
                ownerEntityUrn: o?.ownerEntityUrn || "",
                dailyQuota: o?.dailyQuota || {},
                dailyUsage: o?.dailyUsage || {},
                isLinkedinPremium: Boolean(o?.isLinkedinPremium),
                isDisconnected: false,
                workspaceIds: Array.isArray(o?.workspaceIds) ? o.workspaceIds : [],
                hasLinkedinToken: true,
              };
            };

            if (reconnectTarget) {
              const idx = prev.findIndex(
                (item) => getOwnerIdFromAny(item) === reconnectTarget
              );
              if (idx >= 0) {
                const merged = {
                  ...prev[idx],
                  ...buildRowFromOwner(owner, reconnectTarget),
                };
                const next = [...prev];
                next[idx] = merged;
                return next;
              }
            }

            const matchIdx = prev.findIndex(rowMatchesOwner);
            if (matchIdx >= 0) {
              const merged = {
                ...prev[matchIdx],
                ...buildRowFromOwner(owner, getOwnerIdFromAny(prev[matchIdx])),
              };
              const next = [...prev];
              next[matchIdx] = merged;
              return next;
            }

            if (!nextId) return prev;

            const optimistic = buildRowFromOwner(owner, nextId);
            return [optimistic, ...prev];
          });
          try {
            localStorage.setItem("owner", JSON.stringify(owner));
          } catch {
            // ignore localStorage failures
          }
          // Force refresh even when `page` / `searchQuery` were already at defaults.
          lastKnownOwnerIdRef.current = getOwnerIdFromAny(owner);
          setAccountsRefreshKey((prev) => prev + 1);
          if (wasReconnectFlow) {
            // Reconnect should fully refresh owner-scoped state (same behavior users expected before).
            toast.success("Reconnected successfully. Refreshing page...");
            window.setTimeout(() => {
              window.location.reload();
            }, 600);
          }
        }}
        onDisconnected={(ownerId) => {
          const targetId = String(ownerId || "").trim();
          if (!targetId) return;
          setConnectedAccounts((prev) =>
            prev.map((item) => {
              const itemId = getOwnerIdFromAny(item);
              if (itemId !== targetId) return item;
              return {
                ...item,
                isDisconnected: true,
                hasLinkedinToken: false,
              };
            })
          );
          setAccountsRefreshKey((prev) => prev + 1);
        }}
      />

      <ConnectLinkedinExtensionSidebar
        isOpen={showConnectExtensionSidebar}
        onClose={() => {
          setShowConnectExtensionSidebar(false);
          setIsExtensionDetectionLoading(false);
        }}
        onBack={() => {
          setShowConnectExtensionSidebar(false);
          setIsExtensionDetectionLoading(false);
          setShowConnectModal(true);
        }}
        isLoading={isExtensionDetectionLoading}
      />

      <PurchaseCreditsModal
        isOpen={showPurchaseSeatsModal}
        onRequestClose={() => setShowPurchaseSeatsModal(false)}
      />

      {disconnectConfirm.isOpen ? (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 p-4">
          <div className="relative w-full max-w-md min-w-0 rounded-lg bg-white p-6 shadow-xl">
            <button
              type="button"
              aria-label="Close"
              className="absolute right-4 top-4 flex h-8 w-8 items-center justify-center rounded-full text-gray-500 hover:bg-gray-100 hover:text-gray-700"
              onClick={closeDisconnectModal}
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 14 14"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              >
                <path d="M1 1l12 12M13 1L1 13" />
              </svg>
            </button>
            <p className="pr-8 text-lg font-semibold">Disconnect this account?</p>
            <p className="mt-3 text-sm text-gray-600">
              Leads and campaigns are preserved. Sending/import will stop until reconnect.
            </p>
            {disconnectError ? (
              <p className="mt-3 text-sm text-red-600">{disconnectError}</p>
            ) : null}
            <div className="mt-6 flex justify-end gap-3">
              <Button
                variant="outline"
                size="sm"
                isDisabled={isDisconnecting}
                onClick={closeDisconnectModal}
              >
                No
              </Button>
              <Button
                variant="btn-red"
                size="sm"
                isDisabled={isDisconnecting}
                onClick={confirmDisconnect}
              >
                {isDisconnecting ? "Disconnecting..." : "Yes, disconnect"}
              </Button>
            </div>
          </div>
        </div>
      ) : null}

      {executionModeSwitch.isOpen ? (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 p-4">
          <div className="relative w-full max-w-md min-w-0 rounded-lg bg-white p-6 shadow-xl">
            <button
              type="button"
              aria-label="Close"
              className="absolute right-4 top-4 flex h-8 w-8 items-center justify-center rounded-full text-gray-500 hover:bg-gray-100 hover:text-gray-700"
              onClick={closeExecutionModeSwitchModal}
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 14 14"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              >
                <path d="M1 1l12 12M13 1L1 13" />
              </svg>
            </button>
            <p className="pr-8 text-lg font-semibold">
              Switch to {executionModeSwitch.nextMode === "CLOUD" ? "server" : "Chrome"} automation?
            </p>
            <p className="mt-3 text-sm text-gray-600">
              {executionModeSwitch.nextMode === "CLOUD" ? (
                <>
                  Campaign visits, invites, and messages for{" "}
                  <strong>{executionModeSwitch.name}</strong> will run on the InnLead server instead
                  of Chrome. You can still monitor activity in the extension popup.
                </>
              ) : (
                <>
                  Campaign actions for <strong>{executionModeSwitch.name}</strong> will run in
                  Chrome through the extension. Keep Chrome open and the extension online while
                  campaigns are active.
                </>
              )}
            </p>
            {executionModeSwitchError ? (
              <p className="mt-3 text-sm text-red-600">{executionModeSwitchError}</p>
            ) : null}
            <div className="mt-6 flex justify-end gap-3">
              <Button
                variant="outline"
                size="sm"
                isDisabled={isSwitchingExecutionMode}
                onClick={closeExecutionModeSwitchModal}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                size="sm"
                isDisabled={isSwitchingExecutionMode}
                onClick={confirmExecutionModeSwitch}
              >
                {isSwitchingExecutionMode ? "Switching..." : "Confirm switch"}
              </Button>
            </div>
          </div>
        </div>
      ) : null}

      <SenderLimitsSidebar
        isOpen={isSenderLimitsOpen}
        onClose={() => {
          if (isSavingSenderLimits) return;
          setIsSenderLimitsOpen(false);
        }}
        senderName={selectedSenderForLimits?.name || "Sender"}
        senderAvatarSrc={selectedSenderForLimits?.picture || ""}
        linkedInAvatarVariant="sender"
        value={senderLimits}
        max={DEFAULT_SENDER_DAILY_QUOTA_MAX}
        onChange={setSenderLimits}
        onSave={handleSaveSenderLimits}
        readOnly={isSavingSenderLimits}
      />
    </section>
  );
}

